import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  Alert,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Grid,
  Input,
  Rate,
  Select,
  Space,
  Tag,
  Timeline,
  message
} from 'antd'
import {
  growthOpportunitiesUrl,
  growthOpportunityUrl,
  growthPlacesUrl,
  patchRequest,
  postRequest
} from '../../config'
import {
  KIND_LABELS,
  REACH_LABELS,
  STATUS_LABELS,
  countryOptions,
  toOpportunityPayload
} from './utils'

const DATE_FIELDS = ['startsOn', 'endsOn', 'deadlineOn']
const COUNTRIES = countryOptions()

// One opportunity: what's stored, its history, and the form to correct it — or an empty form to add
// one heard about elsewhere (spec: 0042_growth_bot Req 2.4–2.6, 2a.7).
const OpportunityDrawer = ({ open, record, partner, onClose, onSaved }) => {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const screens = Grid.useBreakpoint()
  const isNew = !record
  const kind = Form.useWatch('kind', form)

  useEffect(() => {
    if (!open) return
    form.resetFields()
    if (record) {
      const values = { ...record }
      DATE_FIELDS.forEach(f => {
        values[f] = record[f] ? moment(record[f]) : null
      })
      form.setFieldsValue(values)
    } else {
      form.setFieldsValue({ kind: partner ? 'partner' : 'car_event', fitScore: 3 })
    }
  }, [open, record, partner, form])

  const showErrors = error => {
    const data = error?.response?.data || {}
    const fields = Object.entries(data)
      .filter(([key]) => key !== 'detail')
      .map(([name, errors]) => ({ name, errors: [].concat(errors).map(String) }))
    if (fields.length) form.setFields(fields)
    if (data.detail) message.error(data.detail)
  }

  const save = async () => {
    const values = await form.validateFields()
    const payload = toOpportunityPayload(values)
    const response = isNew
      ? await postRequest(growthOpportunitiesUrl, payload, 'opportunity', setSaving)
      : await patchRequest(growthOpportunityUrl(record.id), payload, 'opportunity', setSaving)
    if (response.success) {
      message.success(isNew ? 'Added.' : 'Saved.')
      onSaved()
    } else {
      showErrors(response.error)
    }
  }

  const putInReach = async withCity => {
    const payload = {
      countryCode: record.countryCode,
      city: withCity ? record.city : '',
      reachable: true,
      note: `Added from ${record.name}`
    }
    const response = await postRequest(growthPlacesUrl, payload, 'place', () => {})
    if (response.success) {
      message.success(`${withCity ? record.city : record.countryName} is now in reach.`)
      onSaved()
    } else {
      const detail = response.error?.response?.data?.city || response.error?.response?.data?.detail
      if (detail) message.error(String(detail))
    }
  }

  const isPartner = kind === 'partner'

  return (
    <Drawer
      title={isNew ? 'Add opportunity' : record.name}
      open={open}
      onClose={onClose}
      width={screens.md ? 560 : '100%'}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={save}>
            {isNew ? 'Add' : 'Save'}
          </Button>
        </Space>
      }
    >
      {!isNew ? (
        <>
          <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Status">{STATUS_LABELS[record.status] || record.status}</Descriptions.Item>
            <Descriptions.Item label="Reach">
              {REACH_LABELS[record.reach] ? (
                <Tag color={REACH_LABELS[record.reach].color}>{REACH_LABELS[record.reach].label}</Tag>
              ) : null}
            </Descriptions.Item>
            <Descriptions.Item label="Found by">
              {record.source === 'team' ? 'Our team' : 'The search'}
            </Descriptions.Item>
            <Descriptions.Item label="First sent">
              {record.firstSentAt ? moment(record.firstSentAt).format('D MMM YYYY, HH:mm') : 'Not yet'}
            </Descriptions.Item>
            <Descriptions.Item label="Last sent">
              {record.lastSentAt ? moment(record.lastSentAt).format('D MMM YYYY, HH:mm') : 'Not yet'}
            </Descriptions.Item>
          </Descriptions>
          {record.reach === 'out' ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Out of reach, and no funded route, so it isn't sent."
              description={
                <Space wrap style={{ marginTop: 8 }}>
                  {record.city ? (
                    <Button size="small" onClick={() => putInReach(true)}>
                      Put {record.city} in reach
                    </Button>
                  ) : null}
                  <Button size="small" onClick={() => putInReach(false)}>
                    Put {record.countryName} in reach
                  </Button>
                </Space>
              }
            />
          ) : null}
          {record.decisions && record.decisions.length ? (
            <Timeline style={{ marginBottom: 8 }}>
              {record.decisions.map(d => (
                <Timeline.Item key={`${d.action}-${d.at}`}>
                  {STATUS_LABELS[d.action] || d.action}
                  {d.days ? ` (${d.days} days)` : ''} · {moment(d.at).format('D MMM, HH:mm')}
                  {d.by ? ` · ${d.by}` : ''}
                </Timeline.Item>
              ))}
            </Timeline>
          ) : null}
        </>
      ) : null}

      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Give it a name.' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="kind" label="Kind" rules={[{ required: true }]}>
          <Select options={Object.entries(KIND_LABELS).map(([value, label]) => ({ value, label }))} />
        </Form.Item>
        <Form.Item name="summary" label="What it is">
          <Input />
        </Form.Item>
        {!isPartner ? (
          <Space wrap>
            <Form.Item name="startsOn" label="Starts" rules={[{ required: true, message: 'When does it start?' }]}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="endsOn" label="Ends">
              <DatePicker />
            </Form.Item>
          </Space>
        ) : null}
        <Space wrap>
          <Form.Item name="city" label="City">
            <Input />
          </Form.Item>
          <Form.Item
            name="countryCode"
            label="Country"
            rules={[{ required: true, message: 'Pick a country.' }]}
            style={{ minWidth: 220 }}
          >
            <Select showSearch optionFilterProp="label" options={COUNTRIES} />
          </Form.Item>
        </Space>
        <Form.Item
          name="url"
          label="Link"
          rules={[{ required: true, type: 'url', message: 'A link to the page it was found on.' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="fitScore" label="Fit">
          <Rate />
        </Form.Item>
        <Form.Item name="fitReason" label="Why it fits">
          <Input />
        </Form.Item>
        {!isPartner ? (
          <>
            <Form.Item name="costNote" label="Cost">
              <Input />
            </Form.Item>
            <Space wrap>
              <Form.Item name="deadlineOn" label="Deadline">
                <DatePicker />
              </Form.Item>
              <Form.Item name="deadlineNote" label="Deadline for">
                <Input />
              </Form.Item>
            </Space>
            <Form.Item name="fundedRoute" label="Funded route" extra="Hosted buyer, trade mission, sponsor… Needed when it's out of reach.">
              <Input />
            </Form.Item>
            <Form.Item name="contentIdea" label="Content idea">
              <Input.TextArea autoSize={{ minRows: 2 }} />
            </Form.Item>
          </>
        ) : null}
      </Form>
    </Drawer>
  )
}

OpportunityDrawer.propTypes = {
  open: PropTypes.bool,
  record: PropTypes.object,
  partner: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
}

export default OpportunityDrawer
